/**
 * Cookie-consent helper used by tracking integrations.
 *
 * The banner component (`CookieConsent.jsx`) writes the consent doc to
 * localStorage and fires the `studiomv:consent-change` CustomEvent. This module
 * is the single read-side for any code that needs to gate on consent — keeps
 * the contract in ONE place so we cannot accidentally fire trackers before
 * the user has chosen.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "studiomv_cookie_consent";
const CONSENT_VERSION = 1;
const TTL_DAYS = 365;
const EVENT = "studiomv:consent-change";

const DEFAULT = { essential: true, statistics: false, marketing: false, decided: false };

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return { ...DEFAULT };
    const age = (Date.now() - new Date(parsed.ts).getTime()) / 86400000;
    if (age > TTL_DAYS) return { ...DEFAULT };
    return {
      essential: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
      decided: true,
    };
  } catch {
    return { ...DEFAULT };
  }
}

export function useConsent() {
  const [consent, setConsent] = useState(getConsent);
  useEffect(() => {
    const handler = () => setConsent(getConsent());
    window.addEventListener(EVENT, handler);
    // Also re-sync when other tabs change consent (StorageEvent)
    const storage = (e) => { if (e.key === STORAGE_KEY) setConsent(getConsent()); };
    window.addEventListener("storage", storage);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", storage);
    };
  }, []);
  return consent;
}
